import { cn } from '@/lib/utils.ts'
import { type ComponentProps, type ForwardRefExoticComponent, type RefAttributes, useEffect, useState } from 'react'
import TextareaAutosize from 'react-textarea-autosize'

import { EditableComponent } from '@/components/EditableComponent.tsx'
import { useMutateStory } from '@/requests/mutations/useMutateStory.ts'
import { isTemporaryId } from '@/utils/key.ts'
import { onEnterInclusive } from '@/utils/keyboard.ts'

type EditableVersionProps = {
  storyId: string
  value?: string
  callback?: () => void
}

export const EditableTitle = ({ storyId, value: _value = '', callback }: EditableVersionProps) => {
  const [value, setValue] = useState(_value ?? '')
  const { mutate } = useMutateStory(storyId)

  useEffect(() => {
    setValue(_value)
  }, [_value])

  return (
    <TextareaAutosize
      id={`title-${storyId}`}
      aria-label="title"
      className={cn(
        'font-EN-KR-body2-regular placeholder:text-contents-placeholder bg-surface-componentBackground border-transparent',
        'flex w-full rounded-xs border text-base ring-offset-background',
        'focus-visible:ring-1 focus-visible:ring-blue-500 focus-visible:ring-offset-0 focus:outline-none focus:border-0.5 focus:border-border-default-focused',
      )}
      value={value}
      onChange={(e) => setValue(e.target.value)}
      onKeyDown={(e) =>
        onEnterInclusive(e, () => {
          e.preventDefault()
          if (value.trim() && (value !== _value || isTemporaryId(storyId)))
            mutate({ title: value }).then(() => callback?.())
        })
      }
      onBlur={() => value && value !== _value && mutate({ title: value })}
      style={{
        width: '100%',
        fontFamily: 'inherit',
        fontSize: '14px',
        lineHeight: '14px',
        padding: '4px',
        margin: '4px 0',
        border: 'none',
        outline: 'none',
        resize: 'none',
        overflow: 'hidden',
      }}
    />
  )
}

export const TypedEditableTitle = EditableComponent as unknown as ForwardRefExoticComponent<
  EditableProps<string, undefined, ComponentProps<typeof EditableTitle>> & RefAttributes<EditableCommands>
>
